import { ExternalLink, FileText, Globe, Linkedin, StickyNote, CheckSquare, Activity } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Badge } from '@/components/ui/badge';
import type { ChatSource } from '@/types/leadChat';
import { useState } from 'react';

interface SourcesListProps {
  sources: ChatSource[];
}

const getSourceIcon = (type: ChatSource['type']) => {
  switch (type) {
    case 'linkedin':
      return <Linkedin className="w-3 h-3" />;
    case 'web_search':
      return <Globe className="w-3 h-3" />;
    case 'url':
      return <ExternalLink className="w-3 h-3" />;
    case 'notes':
      return <StickyNote className="w-3 h-3" />;
    case 'tasks':
      return <CheckSquare className="w-3 h-3" />;
    case 'activities':
      return <Activity className="w-3 h-3" />;
    default:
      return <FileText className="w-3 h-3" />;
  }
};

const getSourceTypeLabel = (type: ChatSource['type']) => {
  switch (type) {
    case 'linkedin':
      return 'LinkedIn';
    case 'web_search':
      return 'Web Search';
    case 'url':
      return 'URL';
    case 'notes':
      return 'Notes';
    case 'tasks':
      return 'Tasks';
    case 'activities':
      return 'Activities';
    default:
      return 'Lead Data';
  }
};

export const SourcesList = ({ sources }: SourcesListProps) => {
  const [isOpen, setIsOpen] = useState(false);

  if (!sources || sources.length === 0) return null;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="mt-2">
      <CollapsibleTrigger className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors">
        <span>Sources</span>
        <Badge variant="secondary" className="h-4 px-1.5 text-xs">
          {sources.length}
        </Badge>
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-2 space-y-1">
        {sources.map((source, index) => (
          <div
            key={index}
            className="flex items-start gap-2 p-2 rounded-md bg-muted/50 text-xs"
          >
            <div className="mt-0.5 text-muted-foreground">
              {getSourceIcon(source.type)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="h-4 px-1.5 text-[10px] font-normal">
                  {getSourceTypeLabel(source.type)}
                </Badge>
                {source.url && (
                  <a
                    href={source.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline truncate"
                  >
                    {source.title}
                  </a>
                )}
                {!source.url && (
                  <span className="text-foreground truncate">{source.title}</span>
                )}
              </div>
              {source.description && (
                <p className="mt-1 text-muted-foreground line-clamp-2">
                  {source.description}
                </p>
              )}
            </div>
          </div>
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
};
